import * as React from 'react';
import { AppContentCardType } from '../../webgiving-generated';
import * as styles from './styles.less';
import Truncate from 'react-truncate';
import { SvgIcon } from '../svg-icon';
import btnPlayIcon from '../../assets/svg/btn-video-play.svg';
import audioPlayerIcon from '../../assets/svg/icon-audio-player.svg';
import moment from 'moment';
import { Text } from '../../components/text';

export interface IAppContentCardPhoneTextProps {
	cardType: AppContentCardType;
	title: string;
	subTitle?: string;
	isFeatured: boolean;
	subtitle?: string;
	summary?: string;
	imageUrl?: string;
	tabName?: string;
	eventStartTime?: Date | null;
	eventEndTime?: Date | null;
	isAllDayEvent?: boolean;
}

export const AppContentCardDisplayCard: React.SFC<IAppContentCardPhoneTextProps> = (props) => {
	const { cardType, title, subtitle, summary, tabName, imageUrl, eventStartTime, eventEndTime, isAllDayEvent } = props;
	const momentStart = eventStartTime && moment(eventStartTime);
	const momentEnd = eventEndTime ? moment(eventEndTime) : null;
	return (
		<div className={styles.card}>
			<div className={styles.imageWrap}>
				{formatImgOverlay(cardType, momentStart, momentEnd)}
				<img src={imageUrl} width={200} alt="" />
			</div>
			<div className={styles.cardTextContent}>
				<h1 className={styles.cardTitle}>{title}</h1>
				{ subtitle && <h2 className={styles.cardSubtitle}>{subtitle}</h2> }
				{ summary &&
					<div className={styles.cardSummary}>
						<Truncate lines={3} trimWhitespace={true}>{formatSummaryText(summary)}</Truncate>
					</div>
				}
				{ eventStartTime &&
					<div className={styles.eventSummary}>{formatEventDateDetails(momentStart as moment.Moment, momentEnd, isAllDayEvent)}</div>
				}
				{ tabName && <div className={styles.cardTabName}>{tabName}</div> }
			</div>
		</div>
	);
};

const formatSummaryText = (summary: string) => {
	const lines = summary.split('\n');
	const lineCount = lines.length;
	return summary.split('\n').map((line, i) => (i === lineCount -1) ? line : <>{line}<br/></>);
};

const formatImgOverlay = (cardType: AppContentCardType, momentStart?: moment.Moment | null, momentEnd?: moment.Moment | null) => {
	switch (cardType) {
		case AppContentCardType.Video:
			return (
				<div className={styles.playButtonOverlay}>
					<SvgIcon scale={0.8} svg={btnPlayIcon} />
				</div>
			);
		case AppContentCardType.Audio:
			return (
				<div className={styles.audioPlayerOverlay}>
					<SvgIcon scale={0.6} svg={audioPlayerIcon} />
				</div>
			);
		case AppContentCardType.Event:
			return momentStart && formatCalendarOverlay(momentStart, momentEnd);
		default:
			return null;
	}
};

const formatCalendarOverlay = (momentStart: moment.Moment, momentEnd?: moment.Moment | null) => {
	if (!momentEnd || momentStart.isSame(momentEnd, 'day')) {
		return (
			<div className={styles.calendarOverlay}>
				<span className={styles.calendarOverlayLargeText}>{momentStart.format('D')}</span>
				<span className={styles.calendarOverlaySmallText}>{momentStart.format('MMM')}</span>
			</div>
		);
	} else if (momentStart.isSame(momentEnd, 'month')) {
		return (
			<div className={styles.calendarOverlay}>
				<span className={styles.calendarOverlayMediumText}>{momentStart.format('D')} - {momentEnd.format('D')}</span>
				<span className={styles.calendarOverlaySmallText}>{momentStart.format('MMM')}</span>
			</div>
		);
	} else {
		return (
			<div className={styles.calendarOverlay}>
				<span className={styles.calendarOverlaySmallText}>{momentStart.format('D MMM')}</span>
				<span className={styles.calendarOverlayDivider}><Text>to</Text></span>
				<span className={styles.calendarOverlaySmallText}>{momentEnd.format('D MMM')}</span>
			</div>
		);
	}
};

const DAY_FORMAT = 'dddd, MMMM D';
const TIME_FORMAT = 'h:mm A';

const formatEventDateDetails = (startTime: moment.Moment, endTime: moment.Moment | null, isAllDayEvent:boolean = false) => {

	if(isAllDayEvent) {
		return (endTime && endTime.isAfter(startTime, 'day')) ? (
				<>
					{startTime.format(DAY_FORMAT)} <Text>to</Text> <br />
					{endTime.format(DAY_FORMAT)}
				</>
			) : startTime.format(DAY_FORMAT);
	}

	if (endTime) {
		if (endTime.isAfter(startTime, 'day')) {
			return (
				<>
					{startTime.format(`${DAY_FORMAT}, ${TIME_FORMAT}`)} <Text>to</Text> <br/>
					{endTime.format(`${DAY_FORMAT}, ${TIME_FORMAT}`)}
				</>
			);
		} else {
			return (
				<>
					{startTime.format(TIME_FORMAT)} - {endTime.format(TIME_FORMAT)} <br />
					{startTime.format(DAY_FORMAT)}
				</>
			);
		}
	} else {
		return startTime.format(`${DAY_FORMAT}, ${TIME_FORMAT}`);
	}
};
